import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { VideoTestData } from '../../types';

interface ABTestRetentionTabContentProps {
  videos: VideoTestData[];
  retentionData: any[];
  winnerVideoId?: string | null;
  winnerVideoName?: string;
}

const RETENTION_COLORS = [
  '#0A84FF',
  '#8A2BE2',
  '#34C759',
  '#FF9500',
  '#FF2D55',
];

const CustomTooltip = ({ active, payload, label }: any) => {
  useLanguage();
  if (active && payload && payload.length) {
    return (
      <div 
        className="rounded-lg p-3 shadow-lg border"
        style={{ 
          background: 'white',
          borderColor: '#E5E7EB'
        }}
      >
        <p className="text-[13px] font-medium mb-2" style={{ color: '#374151' }}>
          {label}{translateInterfaceText("s do vídeo")}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-[12px]">
            <div 
              className="w-2.5 h-2.5 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span style={{ color: '#6B7280' }}>{entry.name}:</span>
            <span className="font-semibold" style={{ color: '#111827' }}>
              {typeof entry.value === 'number' && isFinite(entry.value) ? entry.value.toFixed(1) : '0.0'}%
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export function ABTestRetentionTabContent({ 
  videos,
  retentionData,
  winnerVideoId,
  winnerVideoName
}: ABTestRetentionTabContentProps) {
  useLanguage();
  const [visibleVideos, setVisibleVideos] = useState<Set<string>>(
    new Set(videos.map(v => v.id))
  );

  const toggleVideo = (videoId: string) => {
    setVisibleVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  const filteredData = retentionData.map(point => {
    const filtered: any = { time: point.time };
    videos.forEach(video => {
      if (visibleVideos.has(video.id)) {
        filtered[video.id] = point[video.id];
      }
    });
    return filtered;
  });

  const visibleVideosList = videos.filter(v => visibleVideos.has(v.id));

  return (
    <div className="space-y-6">
      <div 
        className="bg-white rounded-[16px] p-8 border border-[#E5E7EB]"
        style={{ boxShadow: '0px 6px 18px rgba(0, 0, 0, 0.035)' }}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-foreground mb-1">
            {translateInterfaceText("Retenção ao longo do tempo")}</h2>
          <p className="text-sm text-muted-foreground">
            {translateInterfaceText("Visualize como a retenção evoluiu entre os diferentes vídeos testados.")}</p>
        </div>

        {/* Divider */}
        <div className="border-b border-[#EEF2F6] mb-6" />

        {/* Legend with toggles */}
        <div className="flex flex-wrap gap-3 mb-6">
          {videos.map((video, index) => (
            <div
              key={video.id}
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => toggleVideo(video.id)}
            >
              {/* <Checkbox
                checked={visibleVideos.has(video.id)}
                onCheckedChange={() => toggleVideo(video.id)}
              /> */}
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: RETENTION_COLORS[index % RETENTION_COLORS.length] }}
              />
              <span className="text-sm text-foreground">
                {video.name}
              </span>
              {winnerVideoId === video.id && (
                <Badge className="bg-[#2D7FF9] text-white text-[10px] font-semibold rounded-[8px] px-1.5 py-0">
                  {translateInterfaceText("Vencendo")}</Badge>
              )}
            </div>
          ))}
        </div>

        {/* Chart with AreaChart and gradients */}
        <div className="w-full h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={filteredData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                {videos.map((video, index) => (
                  <linearGradient key={video.id} id={`gradient-${video.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={RETENTION_COLORS[index % RETENTION_COLORS.length]} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={RETENTION_COLORS[index % RETENTION_COLORS.length]} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis
                dataKey="time"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#9CA3AF' }}
                tickFormatter={(value) => `${value}s`}
              />
              <YAxis
                domain={[0, 100]}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#9CA3AF' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />

              {visibleVideosList.map((video, index) => {
                const colorIndex = videos.findIndex(v => v.id === video.id);
                return (
                  <Area
                    key={video.id}
                    type="monotone"
                    dataKey={video.id}
                    name={video.name}
                    stroke={RETENTION_COLORS[colorIndex % RETENTION_COLORS.length]}
                    fill={`url(#gradient-${video.id})`}
                    strokeWidth={2}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
      </div>
    </div>
  );
}
